import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaChevronRight,
  FaChevronLeft,
  FaTrash,
  FaImages,
} from "react-icons/fa";
import Swal from "sweetalert2";

const rowsPerPage = 10;

const ContactList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [contact, setContact] = useState([]);
  const totalPages = Math.ceil(contact.length / rowsPerPage);

  const paginatedData = contact.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const renderPageNumbers = () => {
    const pages = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }

    return pages.map((page, i) =>
      page === "..." ? (
        <span key={i} className="px-2 text-gray-500 select-none">
          ...
        </span>
      ) : (
        <button
          key={i}
          onClick={() => setCurrentPage(page)}
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            currentPage === page
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {page}
        </button>
      )
    );
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_all_contactUs`
        );
        const data = await response.json();

        setContact(data.data || []);
      } catch (error) {
        console.error("Error fetching Legal", error);
      }
    };

    fetchTeamMembers();
  }, []);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/delete_contactUs/${id}`,
          {
            method: "DELETE",
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          const updatedList = contact.filter((member) => member._id !== id);
          setContact(updatedList);

          const totalPagesAfterDelete = Math.ceil(
            updatedList.length / rowsPerPage
          );
          if (currentPage > totalPagesAfterDelete) {
            setCurrentPage(totalPagesAfterDelete);
          }

          Swal.fire({
            title: "Deleted!",
            text: "Contact has been deleted.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: data.message || "Failed to delete Legal.",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error!",
          text: "Server error while deleting.",
          icon: "error",
        });
      }
    }
  };

  return (
    <div className="p-4 md:p-4 bg-gray-100 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="p-4"
      >
        <h2 className="text-2xl text-center font-bold mb-10 text-blue-500">
          Contact List
        </h2>

        <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200 bg-white w-full">
          <table className="w-full divide-y divide-gray-200 text-sm text-center rounded-xl">
            <thead className="bg-blue-500 text-white text-xs sm:text-sm sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 min-w-[60px]">S.No.</th>
                <th className="px-4 py-3 min-w-[160px] max-w-[200px]">
                  Full Name
                </th>
                <th className="px-4 py-3 min-w-[200px] max-w-[250px]">Email</th>
                <th className="px-4 py-3 min-w-[140px] max-w-[160px]">Phone</th>
                <th className="px-4 py-3 min-w-[250px] max-w-[300px]">
                  Message
                </th>
                <th className="px-4 py-3 min-w-[100px]">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => (
                  <motion.tr
                    key={index}
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: "spring", stiffness: 100 }}
                    className={`transition-all ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-blue-50`}
                  >
                    <td className="px-2 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-3 text-gray-800 break-words truncate max-w-[200px]">
                      {item.fullname.replace(/\s*#\d+$/, "")}
                    </td>
                    <td className="px-4 py-3 text-blue-600 break-words underline underline-offset-2 truncate max-w-[250px]">
                      {item.email}
                    </td>
                    <td className="px-4 py-3 text-gray-700 break-words truncate max-w-[160px]">
                      {item.mobile_number}
                    </td>
                    <td className="px-4 py-3 text-gray-600 break-words max-w-[300px]">
                      {item.message}
                    </td>
                    <td className="py-3 px-4 flex justify-center items-center">
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="text-red-600 hover:text-red-800 transition"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="14" className="py-6 text-gray-500 text-center">
                    <div className="flex flex-col items-center justify-center py-20">
                      <FaImages className="text-5xl text-gray-300 mb-4" />
                      <p className="text-xl text-gray-500 font-medium">
                        No Contact available.
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Once Contact are uploaded, they’ll show up here.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {contact.length > rowsPerPage && (
          <div className="flex justify-center items-center mt-6 gap-2 flex-wrap">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              <FaChevronLeft size={14} />
            </button>
            {renderPageNumbers()}
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              <FaChevronRight size={14} />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ContactList;
